import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { Angebotsvorlage, ApiClient, PositionsStand,
              Unternehmen } from "@kundeninfo/api-client";
import { Aufklapper, useKopffaltung } from "./Kopffaltung.js";
import { PositionsEditor } from "./PositionsEditor.js";
import type { PositionsEditorClient } from "./PositionsEditor.js";
import { Zahlplan } from "./Zahlplan.js";
import { apiMeldung } from "./vorgangsdetail.js";
import { formatBetrag, formatDatum, formatDezimalBetrag,
         formatMenge } from "../format.js";
import { KiKnopf } from "./meHuellen.js";
import { Chip } from "@kundeninfo/ui/me-design";
import "./VorlageDetail.css";

/**
 * Die VORLAGENSICHT — ein Angebotsdetail ohne Kunden (Migration 029, Ruling V1 des Plans).
 *
 * **Warum ein eigener Baustein und kein Modus von `AngebotDetail`.** Die Spec lässt beides
 * zu und macht zur Pflicht, dass Kopffaltung, `PlatzhalterTextfeld`, `PositionsEditor` und
 * der Modalrahmen UNVERÄNDERT wiederverwendet werden. Genau das leistet diese Datei — und
 * nur sie: `AngebotDetail.tsx` hat 1478 Zeilen und siebzehn Stellen mit `kundeId`, und ihr
 * Inhalt ist zu grossen Teilen kundenbezogen (Parteien, Zahlplan, Statusknöpfe samt
 * Übergangstabelle, Versionsabschnitt mit „Als PDF" je Version, „Kopieren", „Auftrag
 * anlegen", die PDF-Befundliste). Ein `modus: "vorlage"` wären rund zwanzig Verzweigungen
 * in dieser Datei, von denen jede einzelne auch die ANGEBOTSSICHT neu zu prüfen zwänge.
 *
 * Wiederverwendet wird stattdessen, was ohnehin schon herausgelöst ist:
 * `Kopfblock`/`useKopffaltung` (Kopffaltung.tsx), `PlatzhalterTextfeld` und
 * `PositionsEditor` (PositionsEditor.tsx), `Modal`/`ModalFuss` (@kundeninfo/ui). Es
 * entsteht KEIN zweiter Editor und KEINE zweite Textmaske — das war der Anlass des
 * ganzen Pakets.
 *
 * **Was hier NICHT steht und warum.** Keine Statusknöpfe (eine Vorlage bleibt Entwurf),
 * keine Versionen (sie ist ihre eigene Reihe), kein „Als PDF" (Paket 2, Belegvorschau),
 * kein Parteien-Kasten (sie hat keine). Die PERIODENREGEL ist dagegen da: Sie ist ein
 * Kopffeld und wird im Modal gepflegt.
 *
 * **Der ZAHLPLAN-Kasten ist da** (Ruling V5, Aufgabe 9): Er gehört zur Vorlage und reist
 * beim Anwenden mit; dafür entstand die dritte Routenfamilie ohne Kunden
 * (`/api/verwaltung/angebotsvorlagen/:id/zahlplan`). Er steht zwischen den Positionen und
 * den Aktionen, so wie er am Angebot zwischen Versionen und Aktionen steht — LESEND hinter
 * einem geschlossenen Aufklapper, BEARBEITBAR im Kopf-Modal
 * (`views/verwaltung/angebotsvorlagen.tsx`), genau wie am Angebot. Ein Parteien-Kasten
 * bleibt weiterhin draussen: Eine Vorlage hat keine Parteien, aber sie hat eine
 * Zahlungsvereinbarung.
 *
 * **Die Rechnung ist die des SERVERS** (Ruling V2): Zeilennettos, Zwischensummen und die
 * Summe kommen aus `listItems` mit dem Vorlagenbezug — derselben Antwort, die auch der
 * Editor benutzt. Die Maske addiert nichts; `views/verwaltung/vorlagenpositionen.ts` (die
 * lokale Rechenfunktion vom Vormittag des 23.09.) ist damit entfallen.
 *
 * Eigener CSS-Block `vorl__` in einer EIGENEN Datei — mit keiner Ansicht geteilt
 * (Kollisions-Lehre 01.09.). Die wiederverwendeten Bausteine bringen ihre Blöcke selbst mit
 * (`kfal__`, `pe__`, `ki-modal__`) und werden nicht umbenannt.
 */

/** Die Wege, die diese Sicht braucht: das LESEN des Positionsstands, das LESEN des
 *  Zahlplans und — für den Editor darin — dieselbe Reihe, mit der auch das Angebot
 *  arbeitet. GESCHRIEBEN wird der Zahlplan im Kopf-Modal, nicht hier. */
export type VorlageDetailClient = Pick<ApiClient, "listItems" | "getZahlplan">
  & PositionsEditorClient;

export type VorlageDetailProps = {
  client: VorlageDetailClient;
  vorlage: Angebotsvorlage;
  /** Die Unternehmen — für das Kürzel und die Beispielwerte der Vorschau. */
  companies: Unternehmen[];
  /** Die bekannten Platzhalternamen aus der Serverantwort (ohne `#{}`) — als Legende unter
   *  den beiden Rohtexten. `web` darf sie nicht selbst kennen (check-boundaries), und eine
   *  abgetippte Liste wäre eine zweite Wahrheit. */
  platzhalter: string[];
  /** Wird die Vorlage GERADE bearbeitet (Positionen offen)? Die Entscheidung gehört dem
   *  Rahmen — nur so bleibt es bei einer offenen Bearbeitung je Ansicht. */
  bearbeitung: boolean;
  gesperrt: boolean;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  /** Kopf ändern (Modal des Rahmens) bzw. löschen. */
  onKopfAendern: () => void;
  onLoeschen: () => void;
  /** Die Vorlage ist weitergedreht (Version/Betrag aus dem Editor) — der Rahmen schreibt
   *  sie in seine Liste fort und reicht sie als `vorlage` zurück herein. */
  onFortschreiben: (version: number, stand: PositionsStand) => void;
};

/**
 * „2 Positionen" / „1 Position" / „ohne Positionen" — an EINER Stelle, weil die Objektzeile
 * der Liste und die Datenzeile des Details denselben Satz zeigen. Zwei Abschriften liefen
 * beim ersten Plural auseinander.
 */
export function positionsAnzahlText(anzahl: number): string {
  if (anzahl <= 0) return "ohne Positionen";
  return anzahl === 1 ? "1 Position" : `${anzahl} Positionen`;
}

/* Menge und Dezimalbeträge formatiert `format.ts` (`formatMenge`, `formatDezimalBetrag`) —
   dieselbe Stelle, die auch der Positions-Editor und das Rechnungsdetail benutzen. Gerechnet
   wird hier NICHTS (Ruling V2): Die Zahlen der Antwort werden nur gezeigt. Ein fehlender
   Wert (eine Zeile ohne Netto) wird zu „0.00" — die Zeilenart entscheidet ohnehin, ob die
   Zelle überhaupt gezeichnet wird. */

/**
 * Die LESEFASSUNG der Positionen — genau die Zahlen des Servers.
 *
 * Zeilennetto und Zwischensumme kommen aus `zeilennettos` bzw. `zwischensummen` der
 * Antwort; hier wird nichts multipliziert und nichts addiert (Ruling V2). Die Einrückung
 * nach Gliederungsebene steht am Element und nicht als Klasse: Die Tiefe steht erst zur
 * Laufzeit fest, eine Klasse je Ebene wäre eine Obergrenze, die der Vertrag nicht kennt.
 */
function Positionsliste({ stand }: { stand: PositionsStand | null }): ReactElement {
  if (stand === null) return <p className="vorl__leer" role="status">Lädt …</p>;
  if (stand.items.length === 0) {
    return <p className="vorl__leer">Diese Vorlage hat noch keine Positionen.</p>;
  }
  return (
    <>
      <ul className="vorl__liste">
        {stand.items.map((z) => (
          <li key={z.id}
            className={"vorl__zeile"
              + (z.kind === "TEXT" ? " vorl__zeile--text" : "")
              + (z.kind === "SUBTOTAL" ? " vorl__zeile--summe" : "")}
            style={{ paddingLeft: `${10 + z.level * 16}px` }}>
            <span className="vorl__zeile-name">{z.name}</span>
            {z.kind === "ITEM" && (
              <span className="vorl__zeile-rechnung ki-num">
                {/* Ohne Einheit hinge sonst ein Leerzeichen vor dem „×" (Befund
                    Gesamtreview 23.09.2026). */}
                {[formatMenge(z.quantity), z.unit ?? ""].filter((t) => t !== "").join(" ")}
                {" × "}{formatBetrag(z.netUnitPrice, "EUR")}
                {" = "}{formatDezimalBetrag(stand.zeilennettos[z.id] ?? "0", "EUR")}
              </span>
            )}
            {z.kind === "SUBTOTAL" && (
              <span className="vorl__zeile-rechnung ki-num">
                {formatDezimalBetrag(stand.zwischensummen[z.id] ?? "0", "EUR")}
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className="vorl__summe">
        <span>Summe netto</span>
        <span className="ki-num">
          {formatDezimalBetrag(stand.steuersummen.gesamtNetto, "EUR")}
        </span>
      </div>
    </>
  );
}

/**
 * Die BEISPIELWERTE der Platzhalter-Vorschau an einer Vorlage (Spec, Abschnitt
 * Oberfläche).
 *
 * An einer Vorlage gibt es keinen Kunden und keinen Ansprechpartner, aus denen sich
 * `#{kunde.name}` auflösen liesse. Ohne Werte bliebe jedes Token stehen, und der Bearbeiter
 * sähe nie, wie sein Satz aussieht. Mit ERFUNDENEN Werten sieht er es — und die Maske sagt
 * ausdrücklich, dass es Beispielwerte sind (sonst hielte jemand „Muster GmbH" für einen
 * Kunden, der gleich auf dem Beleg steht).
 *
 * Der Firmenname ist der EINZIGE echte Wert: Er steht an der Vorlage.
 *
 * **Was bewusst ROH stehenbleibt — und warum.**
 *
 *  - `firma.anschrift` und `firma.ustId` sind ECHTE Angaben eines ECHTEN Unternehmens. Sie
 *    stehen an den Unternehmens-Stammdaten (Migration 010, `UnternehmenStammdaten` aus
 *    `listCompaniesMaster`, Verwaltungsbereich Unternehmen) und nicht am `Unternehmen` der
 *    Sitzung, das diese Sicht bekommt: Der Typ führt id, Schlüssel, Name, Kürzel, Farbe und
 *    Logos — mehr nicht. Erfinden lassen sie sich nicht: „Muster GmbH" ist zulässig, WEIL es
 *    zur Vorlage keinen Kunden gibt; eine erfundene Anschrift oder Steuernummer für ein
 *    bestehendes Unternehmen wäre dagegen eine Falschangabe auf dem Bildschirm — und beim
 *    Drucken setzt die Engine ohnehin die gepflegten Werte ein. Das Token bleibt deshalb
 *    sichtbar stehen: die ehrliche Lücke.
 *  - `rechnung.nummer`/`rechnung.datum` und die drei `laufzeit.*` entstehen erst AM BELEG
 *    (Nummernkreis, Belegdatum, Laufzeitfelder des Angebots). An einer Vorlage gibt es sie
 *    nicht einmal als Beispiel — der Editor am Angebot löst `laufzeit.*` aus den dortigen
 *    Feldern auf (`laufzeitVorschauWerte`), und genau die hat eine Vorlage nicht.
 */
export function vorlagenVorschauWerte(
  companies: Unternehmen[], companyId: string,
): Record<string, string> {
  const firma = companies.find((c) => c.id === companyId);
  return {
    "kunde.name": "Muster GmbH",
    "kunde.anschrift": "Musterweg 1, 12345 Musterstadt",
    "ansprechpartner.name": "Erika Muster",
    "ansprechpartner.anrede": "Sehr geehrte Frau Muster",
    "angebot.nummer": "AN-2026-0001",
    "angebot.gueltigBis": formatDatum(new Date().toISOString()),
    "datum.heute": formatDatum(new Date().toISOString()),
    ...(firma === undefined ? {} : { "firma.name": firma.name }),
  };
}

export function VorlageDetail(p: VorlageDetailProps): ReactElement {
  const { client, vorlage, bearbeitung, gesperrt } = p;
  /** Die Einfaltung des Textblocks — VORGABE zu, wie beim Bearbeiten eines Angebots
   *  (Entscheid Dirk 08.09.2026): Die Arbeit findet an den Positionen statt. */
  const texte = useKopffaltung(false);
  /** Die Einfaltung der Zahlungsvereinbarung — ebenfalls ZU (siehe den Kasten unten). */
  const zahlplanFaltung = useKopffaltung(false);
  /** Der Positionsstand — `null` heisst „noch nicht geladen". Er kommt vom Server und ist
   *  die EINZIGE Rechnung, die diese Sicht zeigt. */
  const [stand, setStand] = useState<PositionsStand | null>(null);
  const [standFehler, setStandFehler] = useState<string | null>(null);

  const kuerzel = p.companies.find((c) => c.id === vorlage.companyId)?.shortCode
    ?? "fremdes Unternehmen";

  /*
   * Der Positionsstand, EINMAL je Vorlage. Abhängig von der KENNUNG und von `version` —
   * letzteres, weil der Editor beim Schliessen eine neue Version gemeldet hat und die
   * Lesefassung darunter sonst den Stand von vorher zeigte.
   *
   * WÄHREND der Bearbeitung wird nicht geholt: Dann hält der Editor den Stand selbst und
   * dreht mit jedem Schritt die Version weiter — ein Abruf je Tastendruck-Schritt wäre eine
   * Abfragewelle für eine Liste, die in dem Moment gar nicht auf dem Bildschirm steht. Beim
   * Schliessen fällt `bearbeitung` und derselbe Effekt holt genau einmal frisch.
   *
   * Der Abbruch-Riegel ist derselbe wie in den Schwestereffekten der Verwaltung: Wer
   * schnell durch die Liste klickt, hat mehrere Abrufe unterwegs, und die Antworten kommen
   * nicht zwingend in der Reihenfolge zurück, in der sie losgeschickt wurden.
   */
  useEffect(() => {
    /*
     * Zurückgesetzt wird VOR dem Riegel, nicht dahinter: Sonst bliebe ein Ladefehler von
     * eben („Die Positionen konnten nicht geladen werden") als roter Satz über dem gerade
     * geöffneten Editor stehen — über einer Maske, die längst ihren eigenen Stand geholt
     * hat und ihre eigenen Fehler meldet (Befund Fixrunde 1 zu Aufgabe 7).
     */
    setStand(null);
    setStandFehler(null);
    if (bearbeitung) return undefined;
    let abgebrochen = false;
    client.listItems({ art: "template", vorlageId: vorlage.id })
      .then((s) => { if (!abgebrochen) setStand(s); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setStandFehler(apiMeldung(
            fehler, "Die Positionen dieser Vorlage konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [client, vorlage.id, vorlage.version, bearbeitung]);

  return (
    <div className="vorl">
      <div className="vorl__kopf">
        <span className="vorl__name">{vorlage.name}</span>
        <Chip className="vorl__marke">{kuerzel}</Chip>
      </div>
      <div className="vorl__daten">
        <span className="vorl__betrag ki-num">
          {formatBetrag(vorlage.netAmount, "EUR")}
        </span>
        <span className="vorl__anzahl">{positionsAnzahlText(vorlage.itemCount)}</span>
        {vorlage.note !== null && <span className="vorl__notiz">{vorlage.note}</span>}
      </div>

      {/* Die beiden Texte LESEND, als ROHTEXT samt ihren Tokens — aufgelöst wird nie
          (Ruling P3B-ENGINE). Hinter einem Aufklapper, weil sie lang sind und die
          Positionen darunter die Arbeit tragen.

          Der Inhalt wird IMMER gezeichnet und nur versteckt (`hidden` im Aufklapper). Der
          Riegel `{faltung.offen && …}`, den der Aufklapper anbietet, ist für Kästen gedacht,
          die beim Öffnen erst LADEN — hier hängt kein Abruf daran: Beide Texte stehen längst
          in der Antwort der Liste. Ein Riegel wäre also Mehraufwand ohne Gegenwert. (Für
          Suche und Vorleser gilt `hidden` wie ein Ausbau aus dem Baum; das ist hier kein
          Argument in die eine oder andere Richtung.) */}
      <Aufklapper faltung={texte} titel="Texte" inhaltId="vorl-texte">
        <span className="vorl__feldname">Einleitungstext</span>
        {vorlage.introText === null
          ? <p className="vorl__leer">ohne Einleitungstext</p>
          : <p className="vorl__rohtext">{vorlage.introText}</p>}
        <span className="vorl__feldname">Ausleitungstext</span>
        {vorlage.outroText === null
          ? <p className="vorl__leer">ohne Ausleitungstext</p>
          : <p className="vorl__rohtext">{vorlage.outroText}</p>}
        {p.platzhalter.length > 0 && (
          <p className="vorl__legende">
            Platzhalter bleiben als Text stehen und werden erst beim Drucken ersetzt:{" "}
            {p.platzhalter.map((name) => `#{${name}}`).join(" · ")}
          </p>
        )}
      </Aufklapper>

      {/* DIE POSITIONEN. Lesend die Serverzahlen, mit „Bearbeiten" derselbe Editor wie am
          Angebot — der Knopf ist die ganze Naht. */}
      <section className="vorl__positionen" aria-label="Positionen der Vorlage">
        <div className="vorl__positionen-kopf">
          <h3 className="vorl__abschnitt">Positionen</h3>
          {!bearbeitung && (
            <KiKnopf deaktiviert={gesperrt} name={`Positionen bearbeiten (${vorlage.name})`}
              onClick={p.onBearbeiten}>
              Positionen bearbeiten
            </KiKnopf>
          )}
        </div>
        {standFehler !== null && <p className="vorl__fehler" role="alert">{standFehler}</p>}
        {bearbeitung ? (
          <PositionsEditor
            client={client}
            bezug={{ art: "template", vorlageId: vorlage.id }}
            companyId={vorlage.companyId}
            version={vorlage.version}
            onVersionChange={p.onFortschreiben} />
        ) : (
          <Positionsliste stand={stand} />
        )}
      </section>

      {/* Die ZAHLUNGSVEREINBARUNG der Vorlage — LESEND, die fertigen Sätze des Servers
          (Ruling V5: Sie gehört zur Vorlage und reist beim Anwenden mit). HINTER EINEM
          AUFKLAPPER, geschlossen, aus DEMSELBEN Grund wie am Angebot (Gesamtreview I2):
          `Zahlplan` ruft in seinem Effekt sofort ab, sobald er gezeichnet wird — bei
          zwanzig Vorlagen in der Liste wäre das ein Abruf je Zeile, den fast niemand
          ansieht. Erst das Öffnen hängt ihn ein.

          Während der POSITIONSBEARBEITUNG steht er nicht da: Dann gehört die Sicht dem
          Editor, und ein lesender Kasten darunter zeigte einen Stand, den der nächste
          Schritt schon überholt hat. */}
      {!bearbeitung && (
        <div className="vorl__zahlplan">
          <Aufklapper faltung={zahlplanFaltung} titel="Zahlungsvereinbarung"
            inhaltId="vorl-zahlplan-inhalt">
            {zahlplanFaltung.offen && (
              /* Der `key` trägt die VERSION, nicht nur die Kennung — und das ist hier
                 nötig, anders als an den beiden Schwestern:

                 `Zahlplan` lädt in seinem Effekt nur, wenn sich der BEZUG ändert
                 (`bezugsSchluessel`), und der Bezug einer Vorlage ändert sich nie. Diese
                 Tafel bleibt aber gemountet, während das Kopf-Modal darüber liegt: Nach
                 „Kopf ändern → Speichern" stünde sonst die Vereinbarung von vorher da,
                 obwohl das Modal sie eben geschrieben hat. Ein neuer `key` wirft den
                 Baustein weg und lässt ihn frisch laden.

                 Die Lesefassung der POSITIONEN löst dasselbe über `vorlage.version` in der
                 Abhängigkeitsliste ihres Effekts (siehe oben); `AngebotDetail` braucht
                 nichts davon, weil dort das Modal die Lesesicht ersetzt und der Kasten
                 beim Schliessen ohnehin neu entsteht. */
              <Zahlplan key={`${vorlage.id}-${vorlage.version}`} client={client}
                bezug={{ art: "template", vorlageId: vorlage.id }}
                idPraefix="vorl-lese" nurLesend />
            )}
          </Aufklapper>
        </div>
      )}

      <div className="vorl__aktionen">
        {bearbeitung ? (
          /* Der Editor schreibt Schritt für Schritt selbst — es gibt hier nichts zu
             speichern, nur ein Fertig. Genau wie am Angebot: Dort liegt der Editor im
             Modal des Kopfes, hier steht er im Detail, und beides schliesst ohne
             Rückfrage, weil kein ungespeicherter Stand existiert. */
          <KiKnopf onClick={p.onSchliessen}>
            Fertig
          </KiKnopf>
        ) : (
          <>
            <KiKnopf deaktiviert={gesperrt} name={`Kopf ändern (${vorlage.name})`}
              onClick={p.onKopfAendern}>
              Kopf ändern
            </KiKnopf>
            <KiKnopf art="gefahr" deaktiviert={gesperrt} name={`${vorlage.name} löschen`}
              onClick={p.onLoeschen}>
              Löschen
            </KiKnopf>
          </>
        )}
      </div>
    </div>
  );
}
